import React, { useEffect, useState } from "react";
import { TarifiedItem } from "model/tarified-item.model";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  deleteTarifiedItemAction,
  updateTarifiedItemAmount,
} from "store/actions/tarification.actions";
import { selectTarificationApplicationSession } from "store/reducers/tarification.reducer";
import { selectUser } from "store/reducers/user-context.reducer";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import { GlobalState } from "store/configure-store";
import {
  selectAttestationRequest,
  selectTarificationSessionForPatient,
} from "store/reducers/patient.reducer";
import NumberFormat from "react-number-format";
import { usePrevious } from "common/hooks/previous.hook";
import { Attestation } from "model/attestion.model";
import { User } from "model/user.model";
import { Col, Container, Row } from "@cc/react-components/Grid";
import Icon from "@cc/react-components/Icon";
import Modal from "@cc/react-components/Modal";
import Button from "@cc/react-components/Button";
import { TarificationApplicationSession } from "model/tarification.model";

interface Props extends React.ButtonHTMLAttributes<HTMLElement> {
  isModalOpen: boolean;
  setModalOpen: (isModalOpen: boolean) => void;
  tarifiedItem: TarifiedItem;
}

const EditTarifiedItem = ({
  isModalOpen,
  setModalOpen,
  tarifiedItem,
  deleteTarifiedItemAction,
  user,
  tSession,
  session,
  attestation,
  updateTarifiedItemAmount,
}: Props & PropsFromDispatch & PropsFromState) => {
  const [t] = useTranslation();
  const isModalOpenPrev = usePrevious(isModalOpen);
  const [editingHonorarium, setEditingHonorarium] = useState(false);
  const [honorarium, setHonorarium] = useState<number>();
  useEffect(() => {
    if (!isModalOpen && isModalOpenPrev) {
      setEditingHonorarium(false);
      setHonorarium(undefined);
    }
  }, [isModalOpen, isModalOpenPrev]);

  const renderPayedAmount = () => {
    const editPayedAmount = () => {
      if (!editingHonorarium) {
        setEditingHonorarium(true);
      }
    };

    const finishEditingPayedAmount = () => {
      if (!honorarium || Math.round(honorarium! * 100) / 100 === 0) {
        setHonorarium(tarifiedItem.honorarium);
      }
      setEditingHonorarium(false);
    };

    const getPayedAmountToRender = () => {
      return honorarium === 0 ? tarifiedItem.honorarium : honorarium;
    };

    return (
      <div onClick={editPayedAmount}>
        <NumberFormat
          size={4}
          defaultValue={tarifiedItem.honorarium}
          value={getPayedAmountToRender()}
          isNumericString={true}
          prefix={editingHonorarium ? "" : "€ "}
          decimalSeparator=","
          decimalScale={2}
          fixedDecimalScale={!editingHonorarium}
          allowNegative={false}
          displayType={editingHonorarium ? "input" : "text"}
          type="tel"
          inputMode="decimal"
          pattern="\d+[\.,]?\d{0,2}"
          min="0.01"
          step="0.01"
          autoFocus
          onValueChange={(values: any) => setHonorarium(values.value)}
          onBlur={finishEditingPayedAmount}
          onKeyDown={(event: any) => {
            if ([9, 13, 27].includes(event.keyCode)) {
              finishEditingPayedAmount();
            }
          }}
        />
        {!editingHonorarium && <Icon size={"1x"} type="edit" />}
      </div>
    );
  };

  const calculateReimbursed = () => {
    if (honorarium != null && honorarium - tarifiedItem.reimbursement <= 0) {
      return "€ " + honorarium;
    } else {
      return "€ " + tarifiedItem.reimbursement;
    }
  };

  const calculatePatientFee = () => {
    if (honorarium != null) {
      return (
        "€ " +
        Math.max(
          Math.round((honorarium - tarifiedItem.reimbursement) * 100) / 100,
          0
        )
      );
    } else {
      return "€ " + tarifiedItem.patientFee;
    }
  };

  return (
    <Modal centered show={isModalOpen} onHide={() => setModalOpen(false)}>
      <Modal.Body>
        <Container>
          <Modal.Title className="my-2">
            {t("tarifiedItemsEdit.title") + " : " + tarifiedItem.code}
          </Modal.Title>
          <Row>
            <Col xs={12}>
              <p className="text-muted">{tarifiedItem.prestationCodeText}</p>
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <p>{t("tarifiedItemsEdit.honorarium")}</p>
            </Col>
            <Col xs={4}>{renderPayedAmount()}</Col>
            <Col xs={8}>
              <p>{t("tarifiedItemsEdit.officialHonorarium")}</p>
            </Col>
            <Col xs={4}>
              <p>{"€ " + tarifiedItem.officialHonorarium}</p>
            </Col>
            <Col xs={8}>
              <p>{t("tarifiedItemsEdit.patientFee")}</p>
            </Col>
            <Col xs={4}>
              <p>{calculatePatientFee()}</p>
            </Col>
            <Col xs={8}>
              <p>{t("tarifiedItemsEdit.reimbursement")}</p>
            </Col>
            <Col xs={4}>
              <p>{calculateReimbursed()}</p>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer className={"w-100 d-flex justify-content-around"}>
        <Button
          variant="borderless-danger"
          onClick={() => {
            setModalOpen(false);
            deleteTarifiedItemAction(
              session,
              user.id,
              tSession.tarificationSessionId,
              tarifiedItem.id
            );
          }}
        >
          {t("action.delete")}
        </Button>
        <Button
          variant="borderless-primary"
          onClick={() => {
            if (!!honorarium && honorarium !== tarifiedItem.honorarium) {
              updateTarifiedItemAmount(
                session,
                user.id,
                tSession.tarificationSessionId,
                tarifiedItem.id,
                honorarium,
                attestation
              );
            }
            setModalOpen(false);
          }}
        >
          {t("action.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface PropsFromDispatch {
  deleteTarifiedItemAction: (
    session: TarificationApplicationSession,
    userId: number,
    sessionId: string,
    tarifiedItemId: string
  ) => void;
  updateTarifiedItemAmount: (
    session: TarificationApplicationSession,
    userId: number,
    sessionId: string,
    tarifiedItemId: string,
    amount: number,
    attestation: Attestation
  ) => void;
}

interface PropsFromState {
  session: TarificationApplicationSession;
  tSession: TarificationSessionKey;
  user: User;
  attestation: Attestation;
}

const mapStateToProps = (state: GlobalState) => {
  return {
    session: selectTarificationApplicationSession(state),
    user: selectUser(state),
    attestation: selectAttestationRequest(state),
    tSession:
      selectTarificationSessionForPatient(state) ||
      new TarificationSessionKey(),
  };
};

export default connect(mapStateToProps, {
  deleteTarifiedItemAction,
  updateTarifiedItemAmount,
})(EditTarifiedItem);
